import React, { useCallback } from 'react';
import {
  createStyles, Grid, makeStyles, Theme,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { replace } from 'connected-react-router';

import yup from '../../../utility/yup';

import {
  FormInput,
  FormSwitch,
  FormSelect,
  MultiEmailField,
  Modal,
} from '../../../components';
import { useTitle } from '../../../hooks';
import { Loadable } from '../../../store/types';
import { UserPermissions, GenericItem } from '../../../types/items';
import { selectLists } from '../../../store/modules/lists/selectors';
import { selectDefaultOptions } from '../../../store/modules/user-permissions/selectors';
import { setOptionSendEmail } from '../../../store/modules/user-permissions/actions';
import { CheckIcon, TimesIcon } from '../../../components/icon/solid';
import SavedSearchService, { SavedSearchPages } from '../../../services/SavedSearchService';

const useStyles = makeStyles(({ colors }: Theme) => createStyles({
  root: {
    marginBottom: '1rem',
  },
  title: {
    color: colors.section,
    fontFamily: 'Montserrat',
    letterSpacing: '0.23px',
    fontSize: '18px',
  },
  status: {
    color: colors.section,
    fontFamily: 'Montserrat',
    letterSpacing: '0.21px',
    fontSize: '16px',
    fontWeight: 500,
  },
  statusBoxSuccess: {
    border: '1px solid #7CAF42',
    borderRadius: '16px',
    boxSizing: 'border-box',
    padding: '8px',
    fontSize: '1rem',
  },
  statusBoxDanger: {
    border: '1px solid #EE4D23',
    borderRadius: '16px',
    boxSizing: 'border-box',
    padding: '8px',
    fontSize: '1rem',
  },
  checkIcon: {
    color: '#7CAF42',
    verticalAlign: 'middle',
    marginRight: '0.5rem',
    fontSize: '1rem',
  },
  timesIcon: {
    color: '#EE4D23',
    verticalAlign: 'middle',
    marginRight: '0.5rem',
    fontSize: '1rem',
  },
}), { name: 'TabTeam' });

const userPermissionSchema = yup.object().shape({
  email: yup.string().email('This must be a valid email.').required('Email is required.').trim(),
});

const multiEmailSchema = yup.object().shape({
  email: yup.string().required().trim(),
});

/* eslint-disable no-param-reassign */

type Props = {
  creatingMode?: boolean;
  record: Loadable<UserPermissions>;
  onSubmit: (record: UserPermissions) => void;
  titleSubmit: string;
  setEmailFromSearch?: boolean;
  setIsOpenModal: (value: boolean) => void;
};

const TeamForm = ({
  creatingMode = false,
  record: { data },
  onSubmit,
  titleSubmit,
  setEmailFromSearch = false,
  setIsOpenModal,
}: Props) => {
  useTitle('Manage Team');
  const classes = useStyles();
  const dispatch = useDispatch();
  const lists = useSelector(selectLists);
  const defaultOptions = useSelector(selectDefaultOptions);
  const { path, params } = useRouteMatch();
  const canEdit = data?.permissions.can_edit || false;

  if (data) {
    if (creatingMode) {
      data.request_registration = true;
      data.request_billing_info = true;
    }
    data.invite = defaultOptions.invite;
    data.cc_sender = false;
  }

  const changeOptionInvite = useCallback((value: boolean) => {
    dispatch(setOptionSendEmail(value));
  }, [dispatch]);

  const getEmail = useCallback(() => {
    if (!setEmailFromSearch) return '';
    if (!lists || !defaultOptions || !data) return '';
    return SavedSearchService.getValue(SavedSearchPages.Teams);
  }, [lists, defaultOptions, data, setEmailFromSearch]);

  const closeHandler = useCallback(() => {
    setIsOpenModal(false);
    if (path.indexOf('admin') !== -1) {
      dispatch(replace(`/dashboard/admin/organizations/${params.id}/team`));
    } else {
      dispatch(replace(`/dashboard/user/organizations/${params.id}/team`));
    }
  }, [dispatch, setIsOpenModal, path, params]);

  return (
    <Modal
      open
      title="Manage Team"
      onCloseHandler={closeHandler}
      form={{
        data,
        validationSchema: creatingMode ? multiEmailSchema : userPermissionSchema,
        onSubmit,
        creatingMode,
        submitTitle: titleSubmit,
      }}
    >
      <Grid container>
        <Grid container spacing={4} className={classes.root}>
          {creatingMode?
            (
              <Grid item xs={12}>
                <MultiEmailField
                  label="Email addresses (comma separated)"
                  name="email"
                  disabled={!canEdit}
                  autoFocus
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <FormInput
                  label="Email"
                  name="email"
                  disabled={!canEdit}
                  getValueAfterInit={getEmail}
                />
              </Grid>
            )
          }
          <Grid item xs={12} sm={6}>
            <FormSelect
              label="Association"
              name="user_association_id"
              disabled={!canEdit}
              items={lists.data ? lists.data['user-associations']
                .map((item):GenericItem<number> => ({ id: item.key, title: item.value })) : null}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormSelect
              label="Permissions"
              name="user_permission_id"
              disabled={!canEdit}
              items={lists.data ? lists.data['user-permissions']
                .map((item):GenericItem<number> => ({ id: item.key, title: item.value })) : null}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} className={classes.root}>
          <Grid item xs={12}>
            <FormSwitch
              label="Send email notification"
              name="invite"
              onChangeCallback={changeOptionInvite}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <h4 className={classes.status}>Status</h4>
          </Grid>
          <Grid item>
            <span
              className={(data && data.ref.user_state.id > 0) ? classes.statusBoxSuccess : classes.statusBoxDanger}
            >
              {(data && data.ref.user_state.id > 0)
                ? (
                  <>
                    <CheckIcon className={classes.checkIcon} />
                    Email Invite Sent
                  </>
                ) : (
                  <>
                    <TimesIcon className={classes.timesIcon} />
                    Email Invite Not Sent
                  </>
                )}
            </span>
          </Grid>
          {(data && data.ref.user_state.id > 0)
          && (
            <Grid item>
              <span className={(data.ref.user_state.id > 1) ? classes.statusBoxSuccess : classes.statusBoxDanger}>
                {(data.ref.user_state.id === 2)
                  ? (
                    <>
                      <CheckIcon className={classes.checkIcon} />
                      Invite Accepted
                    </>
                  ) : (
                    <>
                      <TimesIcon className={classes.timesIcon} />
                      Invite
                      {' '}
                      {data.ref.user_state.id === 3 ? 'Declined' : 'Not Accepted'}
                    </>
                  )}
              </span>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Modal>
  );
};

export default TeamForm;
